import format from "date-fns/format";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Naive local wall-clock time dressed as ISO-UTC — the convention every pickup
 * datetime in this system already uses (see how AddBooking builds `time_stamp`,
 * and how the user app's SearchForm appends `.000Z` to a locally formatted
 * date). Special-price windows are stored the same way, so a comparison is
 * wall clock against wall clock.
 *
 * Converting to a true UTC instant instead would shift every window by the
 * zone's offset — a Riyadh 18:00–22:00 window would start firing at 15:00.
 */
export const toWallClockIso = (value: any): string => {
  if (!value) return "";
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return "";
  return `${format(parsed, "yyyy-MM-dd")}T${format(parsed, "HH:mm:ss")}.000Z`;
};

/**
 * A stored bound turned into the Date a picker should show.
 *
 * The stored value is a naive wall clock dressed as UTC, so it has to be rebuilt
 * as a LOCAL Date carrying the same clock reading. Handing the raw string
 * straight to a picker makes it render the instant in the viewer's zone: an
 * admin in UTC+5 saw a 12:00 window as 17:00, and because `toWallClockIso`
 * formats in local time, saving wrote 17:00 back. Every save shifted the window
 * by the admin's own offset. This is the inverse of `toWallClockIso`, so the
 * pair round-trips exactly.
 */
export const toPickerDate = (value: any): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return null;
  return new Date(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth(),
    parsed.getUTCDate(),
    parsed.getUTCHours(),
    parsed.getUTCMinutes(),
    parsed.getUTCSeconds()
  );
};

/**
 * Combines the separate date and time pickers Add Booking uses into one pickup
 * instant, the same way its submit handler does.
 */
export const toPickupWallClockIso = (date: any, time: any): string => {
  const datePart = date?.date || date;
  const timePart = time?.date || time;
  if (!datePart || !timePart) return "";

  const parsedDate = new Date(datePart);
  const parsedTime = new Date(timePart);
  if (isNaN(parsedDate.getTime()) || isNaN(parsedTime.getTime())) return "";

  return toWallClockIso(
    new Date(`${parsedDate.toDateString()} ${parsedTime.toTimeString()}`)
  );
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Same bound as `readableWindow`, split so the table can print the date at full
 * strength and the clock time muted — the dates are what an admin scans for, the
 * 00:00s are noise until they matter. Weekday is included because surge windows
 * are reasoned about as "the weekend", not as a date.
 *
 * Read as UTC for the same reason `readableWindow` does: these are naive wall
 * clock values, and treating them as instants shifts them by the zone's offset.
 */
export const windowParts = (value: any): { date: string; time: string } => {
  if (!value) return { date: "", time: "" };
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return { date: "", time: "" };
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date:
      `${WEEKDAYS[parsed.getUTCDay()]}, ${pad(parsed.getUTCDate())} ` +
      `${MONTHS[parsed.getUTCMonth()]} ${parsed.getUTCFullYear()}`,
    time: `${pad(parsed.getUTCHours())}:${pad(parsed.getUTCMinutes())}`,
  };
};

/** Display a stored window bound. Must read it back as UTC, or the naive value
 *  gets re-interpreted as an instant and prints a shifted clock. */
export const readableWindow = (value: any): string => {
  if (!value) return "";
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${pad(parsed.getUTCDate())} ${MONTHS[parsed.getUTCMonth()]} ` +
    `${parsed.getUTCFullYear()}, ${pad(parsed.getUTCHours())}:${pad(
      parsed.getUTCMinutes()
    )}`
  );
};
